import React, { useEffect, useRef, useState } from "react";
import { endpoints } from "@/lib/api";
import { MoreHorizontal, Pencil, Trash2, X as XIcon, Check } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * PostActions — three-dot menu shown only on posts authored by the current user.
 * Provides Edit (inline textarea) and Delete (confirm dialog).
 *
 * Props:
 *  - post:       the post document (needs id + content)
 *  - isOwner:    boolean — only render the menu if true
 *  - onChanged:  called after successful edit/delete so the feed can refresh
 */
export default function PostActions({ post, isOwner, onChanged, editing, setEditing }) {
  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const menuRef = useRef(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // The menu is always available — deletion is a curation tool the user
  // should be able to reach on any post at any time. Edit stays gated
  // to `isOwner` inside the menu so nobody misrepresents another author.

  const doDelete = async () => {
    setDeleting(true);
    try {
      await endpoints.deletePost(post.id);
      toast.success("Post removed.");
      setConfirmOpen(false);
      onChanged?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not delete.";
      toast.error(msg);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <div className="relative" ref={menuRef}>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          data-testid={`post-actions-toggle-${post.id}`}
          aria-label="Post actions"
          className="w-8 h-8 rounded-full grid place-items-center hover:bg-[hsl(var(--sand))] text-[hsl(var(--muted-foreground))] tab-link"
        >
          <MoreHorizontal size={16} />
        </button>
        {open && (
          <div
            role="menu"
            data-testid={`post-actions-menu-${post.id}`}
            className="absolute right-0 top-9 z-20 min-w-[140px] rounded-md border hairline bg-[hsl(var(--paper))] shadow-lg overflow-hidden"
          >
            {isOwner && (
              <button
                type="button"
                onClick={() => { setEditing(true); setOpen(false); }}
                data-testid={`post-edit-${post.id}`}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[hsl(var(--sand))] text-left"
              >
                <Pencil size={13} /> Edit
              </button>
            )}
            <button
              type="button"
              onClick={() => { setConfirmOpen(true); setOpen(false); }}
              data-testid={`post-delete-${post.id}`}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[hsl(var(--terracotta))]/10 text-[hsl(var(--terracotta))] text-left"
            >
              <Trash2 size={13} /> Delete
            </button>
          </div>
        )}
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="bg-[hsl(var(--paper))]" data-testid={`post-delete-confirm-${post.id}`}>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">Remove this post?</AlertDialogTitle>
            <AlertDialogDescription>
              It will be gone from the feed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`post-delete-cancel-${post.id}`}>Keep</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              disabled={deleting}
              data-testid={`post-delete-confirm-yes-${post.id}`}
              className="bg-[hsl(var(--terracotta))] hover:bg-[hsl(var(--terracotta))]/90 text-[hsl(var(--paper))]"
            >
              {deleting ? "Removing…" : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/**
 * InlinePostEditor — replaces the post content area with a textarea when editing.
 */
export function InlinePostEditor({ post, onDone, onCancel }) {
  const [value, setValue] = useState(post.content || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const trimmed = value.trim();
    if (!trimmed) { toast.error("Say something."); return; }
    if (trimmed === (post.content || "").trim()) { onCancel?.(); return; }
    setSaving(true);
    try {
      await endpoints.editPost(post.id, { content: trimmed });
      toast.success("Updated.");
      onDone?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not save.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2" data-testid={`post-editor-${post.id}`}>
      <Textarea
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          e.target.style.height = "auto";
          e.target.style.height = Math.min(e.target.scrollHeight, 360) + "px";
        }}
        data-testid={`post-editor-textarea-${post.id}`}
        rows={4}
        className="bg-transparent border-[hsl(var(--border))] focus-visible:ring-[hsl(var(--clay))] min-h-[140px] max-h-[360px] leading-relaxed resize-y"
        autoFocus
      />
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          onClick={save}
          disabled={saving}
          data-testid={`post-editor-save-${post.id}`}
          className="rounded-full bg-[hsl(var(--clay))] hover:bg-[hsl(var(--clay))]/90 text-[hsl(var(--paper))]"
        >
          <Check size={13} className="mr-1" /> {saving ? "Saving…" : "Save"}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={onCancel}
          disabled={saving}
          data-testid={`post-editor-cancel-${post.id}`}
          className="rounded-full"
        >
          <XIcon size={13} className="mr-1" /> Cancel
        </Button>
      </div>
    </div>
  );
}
